import type { ReactNode } from 'react';

import { Button } from '@shared/ui/button';

import { STATUS_SCREEN_TEXT } from './status-screen.text';

/**
 * 화면 전체를 차지하는 상태 안내(오류 · 연결 불가 · 점검 · 권한 없음 · 없는 화면).
 * Figma `Components` 의 `StatusScreen`(2146:29995, DD-15 H8): 가운데 정렬 세로 줄(간격 12, 위아래 40 · 양옆 24)
 * — 제목(heading-3) → 설명(body, muted) → traceId(caption, muted) → 버튼 줄(위 여백 8, 사이 8:
 * secondaryAction = Button neutral md, action = Button primary md).
 * Figma 의 `kind` 축(not-found · unreachable · error · forbidden · maintenance · login-required)은 문구 · 버튼의 예시다.
 * 코드는 kind 를 받지 않고 title · description · action 으로 같은 모양을 만든다(문구는 쓰는 쪽의 `*.text.ts`).
 */
export interface StatusScreenAction {
  label: string;
  onClick: () => void;
}

export interface StatusScreenProps {
  title: string;
  description?: string;
  /** 추적 ID. 있으면 화면에 보인다(신고 · 문의용) */
  traceId?: string;
  action?: StatusScreenAction;
  secondaryAction?: StatusScreenAction;
  /** 페이지 전체가 아니라 구획 안에 넣을 때 */
  inline?: boolean;
  children?: ReactNode;
}

export function StatusScreen({
  title,
  description,
  traceId,
  action,
  secondaryAction,
  inline = false,
  children,
}: StatusScreenProps) {
  const Heading = inline ? 'h2' : 'h1';
  return (
    <section
      role="alert"
      className={
        inline
          ? 'flex flex-col items-center gap-3 px-6 py-10 text-center'
          : 'flex min-h-screen flex-col items-center justify-center gap-3 bg-bg-canvas px-6 py-10 text-center'
      }
    >
      {/* 화면 전체면 그 화면의 제목(h1), 구획 안(inline)이면 구획 아래 제목(h2) */}
      <Heading className="text-fg-default typo-heading-3">{title}</Heading>
      {description ? <p className="text-fg-muted typo-body">{description}</p> : null}
      {children}
      {traceId ? (
        <p className="text-fg-muted typo-caption">
          {STATUS_SCREEN_TEXT.traceId}: <span>{traceId}</span>
        </p>
      ) : null}
      {action || secondaryAction ? (
        <div className="mt-2 flex items-center gap-2">
          {secondaryAction ? (
            <Button variant="neutral" size="md" onClick={secondaryAction.onClick}>
              {secondaryAction.label}
            </Button>
          ) : null}
          {action ? (
            <Button variant="primary" size="md" onClick={action.onClick}>
              {action.label}
            </Button>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
